import React, { useEffect, useState } from "react";
import { getBrokers, saveBroker, editBroker, changeBrokerStatus } from "../../../Services/BrokersApi";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
  Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BrokerTransactionPage from "./BrokerTransactionDialog";

const BrokerMainpage = () => {
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // حالات للنموذج
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [editingBrokerId, setEditingBrokerId] = useState(null); // id السجل (existing)
  const [editingUserId, setEditingUserId] = useState(null); // ← جديد: user_id المستخدم المرتبط بالموزع
  const [statusChangingId, setStatusChangingId] = useState(null); // حالة جديدة لمتابعة تغيير الحالة لموزع محدد
  // حالة فتح مودال عرض إصدارات الموزع
  const [openVersionsDialog, setOpenVersionsDialog] = useState(false);
  const [versionsBrokerId, setVersionsBrokerId] = useState(null);
  const [versionsPayload, setVersionsPayload] = useState(null); // بيانات تمرّر للمكوّن (اختياري)
  const [versionsBrokerInfo, setVersionsBrokerInfo] = useState(null); // كائن الموزع الممرّر لعرضه داخل مكوّن الإصدارات
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    phone: "",
    phone2: "",
    city: "",
    address: "",
    commetion: 0,
    remmaning: 0,
    paied: 0,
    balance: 0,
    insurance: 0,
    broker_code: "",
  });

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoading(true);

        const brokersList = await getBrokers();
        console.log("Fetched brokers data:", brokersList);

        if (mounted) setBrokers(brokersList);
      } catch (e) {
        if (mounted) setError(e?.error || "خطأ في جلب البيانات");
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // فتح مودال إضافة جديد
  const openAdd = () => {
    setEditingBrokerId(null);
    setFormError(null);
    setFormData({
      email: "",
      password: "",
      name: "",
      phone: "",
      phone2: "",
      city: "",
      address: "",
      commetion: 0,
      remmaning: 0,
      paied: 0,
      balance: 0,
      insurance: 0,
      broker_code: "",
    });
    setShowForm(true);
  };

  // فتح مودال تعديل مع تعبئة الحقول بالقيم الحالية
  const openEdit = (b) => {
    setEditingBrokerId(b.id);
    // تعيين user_id إن وجد
    setEditingUserId(b.user?.id || b.user_id || null);
    setFormError(null);
    setFormData({
      email: b.user?.email || "",
      password: "", // لا نعرض كلمة المرور الحالية
      name: b.name || b.user?.name || "",
      phone: b.phone1 || b.user?.phone || "",
      phone2: b.phone2 || "",
      city: b.city || "",
      address: b.address || "",
      commetion: b.commetion != null ? b.commetion : (b.user?.commetion || 0),
      remmaning: b.remmaning != null ? b.remmaning : 0,
      paied: b.paied != null ? b.paied : 0,
      balance: b.balance != null ? b.balance : 0,
      insurance: b.insurance != null ? b.insurance : 0,
      broker_code: b.user?.broker_code || b.broker_code || "",
    });
    setShowForm(true);
  };

  const toggleForm = () => {
    setFormError(null);
    setShowForm((s) => !s);
    if (showForm) setEditingBrokerId(null);
  };

  // تحديث الحقول
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
  };

  // إرسال النموذج (إضافة أو تعديل)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    const token = localStorage.getItem("authToken");

    const payload = {
      ...formData,
      commetion: parseFloat(formData.commetion) || 0,
      remmaning: parseFloat(formData.remmaning) || 0,
      paied: parseFloat(formData.paied) || 0,
      balance: parseFloat(formData.balance) || 0,
      insurance: parseFloat(formData.insurance) || 0,
    };

    try {
      let res;
      if (editingBrokerId) {
        // مرّر user_id إن وُجد بدل id في جسم الطلب (editBroker يدعم كلا الحالتين)
        res = await editBroker(editingBrokerId, editingUserId, payload, token);
        console.log("Broker edited:", res);
      } else {
        res = await saveBroker(payload, token);
        console.log("Broker saved:", res);
      }

      const refreshed = await getBrokers();
      setBrokers(refreshed);
      setShowForm(false);
      setEditingBrokerId(null);
      setEditingUserId(null);

      setFormData({
        email: "",
        password: "",
        name: "",
        phone: "",
        phone2: "",
        city: "",
        address: "",
        commetion: 0,
        remmaning: 0,
        paied: 0,
        balance: 0,
        insurance: 0,
        broker_code: "",
      });
    } catch (err) {
      console.error("Save/Edit broker error:", err);
      // حاول استخراج رسالة الخطأ المفصّلة من استجابة السيرفر (مثال: { error, error })
      const serverData = err?.response?.data;
      const serverMessage = serverData?.error || serverData?.error;
      setFormError(serverMessage || err?.error || JSON.stringify(err) || "خطأ أثناء الحفظ");
    } finally {
      setSubmitting(false);
    }
  };

  // دالة لتغيير/إيقاف حساب الموزع
  const handleChangeStatus = async (b) => {
    const ok = window.confirm(`هل أنت متأكد من تغيير حالة الموزع "${b.name || b.user?.name}"؟`);
    if (!ok) return;

    const token = localStorage.getItem("authToken");
    setStatusChangingId(b.id);

    try {
      // نمرّر user_id إن وُجد، خلاف ذلك نمرّر id فقط
      const userId = b.user?.id || b.user_id || null;
      const res = await changeBrokerStatus(b.id, userId, token);
      console.log("Status changed:", res);
      const refreshed = await getBrokers();
      setBrokers(refreshed);
    } catch (err) {
      console.error("Change status error:", err);
      alert(err?.error || "خطأ أثناء تغيير الحالة");
    } finally {
      setStatusChangingId(null);
    }
  };

  // افتح مودال إصدارات الموزع
  // نمرّر هنا user_id كقيمة رقمية (أو null) في المتغيّر versionsPayload
  const openVersionsModal = (b, userId = null) => {
    const id = b?.id ?? b?.broker_id ?? null;
    if (!id) return;
    setVersionsBrokerId(id);
    setVersionsPayload(userId); // الآن versionsPayload هو رقم user_id أو null
    setVersionsBrokerInfo(b); // خزّن الكائن لتمريره إلى المكوّن
    setOpenVersionsDialog(true);
  };
  const closeVersionsModal = () => {
    setOpenVersionsDialog(false);
    setVersionsBrokerId(null);
    setVersionsPayload(null);
    setVersionsBrokerInfo(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">حدث خطأ: {String(error)}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
          قائمة الموزعين
        </Typography>
        <Chip 
          label={`${brokers.length} موزع`} 
          color="primary" 
          sx={{ fontSize: '1rem', px: 1 }}
        />
      </Box>

      {/* Add Button */}
      <Card sx={{ p: 2, mb: 3, boxShadow: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openAdd}
          sx={{ 
            boxShadow: 2,
            '&:hover': { boxShadow: 4 },
          }}
        >
          إضافة موزع جديد
        </Button>
      </Card>

      {/* Dialog Form */}
      <Dialog 
        open={showForm} 
        onClose={toggleForm}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#1976d2', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon />
            {editingBrokerId ? "تعديل موزع" : "إضافة موزع جديد"}
          </Box>
        </DialogTitle>
        
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {String(formError)}
              </Alert>
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="email"
                  label="البريد الإلكتروني"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="password"
                  type="password"
                  label={editingBrokerId ? "كلمة المرور (اختياري)" : "كلمة المرور"}
                  value={formData.password}
                  onChange={handleChange}
                  required={!editingBrokerId}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="name"
                  label="الاسم"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="phone"
                  label="الهاتف"
                  value={formData.phone}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="phone2"
                  label="الهاتف 2"
                  value={formData.phone2}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="city"
                  label="المدينة"
                  value={formData.city}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="address"
                  label="العنوان"
                  value={formData.address}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="broker_code"
                  label="كود الموزع"
                  value={formData.broker_code}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="commetion"
                  type="number"
                  label="نسبة العمولة"
                  value={formData.commetion}
                  onChange={handleChange}
                  inputProps={{ step: 0.01 }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="balance"
                  type="number"
                  label="الرصيد"
                  value={formData.balance}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="remmaning"
                  type="number"
                  label="المتبقي"
                  value={formData.remmaning}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="paied"
                  type="number"
                  label="المدفوع"
                  value={formData.paied}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="insurance"
                  type="number"
                  label="التأمين"
                  value={formData.insurance}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button onClick={toggleForm} variant="outlined">
              إلغاء
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {submitting ? "جاري الحفظ..." : editingBrokerId ? "حفظ التعديل" : "حفظ الموزع"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog: عرض إصدارات الموزع */}
      <Dialog open={openVersionsDialog} onClose={closeVersionsModal} fullWidth maxWidth="lg">
        <DialogTitle sx={{ background: "linear-gradient(135deg,#ff9a56 0%,#ff6a00 100%)", color: "#fff" }}>
          إصدارات الموزع #{versionsBrokerId}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {/* نمرّر brokerId و initialUserId و brokerInfo (كائن الموزع) للمكوّن */}
          {versionsBrokerId && (
            <BrokerTransactionPage
              brokerId={versionsBrokerId}
              initialUserId={versionsPayload}
              brokerInfo={versionsBrokerInfo}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeVersionsModal}>إغلاق</Button>
        </DialogActions>
      </Dialog>

      {/* Table */}
      {brokers.length === 0 ? (
        <Card sx={{ p: 8, textAlign: 'center', boxShadow: 3 }}>
          <Typography variant="h5" color="text.secondary">
            لا توجد بيانات
          </Typography>
        </Card>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#1976d2' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>الاسم</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>الهاتف</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>المدينة</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>الرصيد</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>المتبقي</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>مدفوع</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>التأمين</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>الحالة</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>إجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {brokers.map((b, index) => (
                <TableRow 
                  key={b.id}
                  onClick={() => openVersionsModal(b, b.user?.id ?? b.user_id ?? null)} // تمرير user_id كقيمة رقمية
                  sx={{
                    backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                      transition: 'background-color 0.3s',
                    },
                    cursor: 'pointer', // مؤشر قابلية النقر
                  }}
                >
                  <TableCell sx={{ fontWeight: 600 }}>{b.name || b.user?.name}</TableCell>
                  <TableCell>{b.phone1 || b.user?.phone}</TableCell>
                  <TableCell>{b.city}</TableCell>
                  <TableCell>{b.balance}</TableCell>
                  <TableCell>{b.remmaning}</TableCell>
                  <TableCell>{b.paied}</TableCell>
                  <TableCell>{b.insurance}</TableCell>
                  <TableCell>
                    <Chip
                      label={b.user?.active ? "مفعل" : "معطل"}
                      size="small"
                      color={b.user?.active ? "success" : "error"}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => { e.stopPropagation(); openEdit(b); }} // منع انتشار الحدث للصف
                      title="تعديل"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {/* زر لفتح مودال إصدارات الموزع - يبقى متاح لكن يمنع انتشار الحدث */}
                    <IconButton
                      size="small"
                      color="info"
                      onClick={(e) => {
                        e.stopPropagation();
                        openVersionsModal(b, b.user?.id ?? b.user_id ?? null); // تمرير user_id كقيمة رقمية
                      }}
                      title="عرض الإصدارات"
                      sx={{ ml: 0.5 }}
                    >
                      <AccountTreeIcon fontSize="small" />
                    </IconButton>
                   <IconButton
                     size="small"
                     color={b.user?.active ? "error" : "success"}
                     onClick={(e) => { e.stopPropagation(); handleChangeStatus(b); }} // منع انتشار الحدث
                     disabled={statusChangingId === b.id}
                     title={b.user?.active ? "إيقاف الحساب" : "تفعيل الحساب"}
                   >
                     {statusChangingId === b.id ? (
                       <CircularProgress size={20} />
                     ) : b.user?.active ? (
                       <BlockIcon fontSize="small" />
                     ) : (
                       <CheckCircleIcon fontSize="small" />
                     )}
                   </IconButton>
                 </TableCell>
               </TableRow>
             ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default BrokerMainpage;
